import React, { useState, useEffect } from 'react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, Edit2, Save, X, Loader2, Users, Crown, Briefcase, User, Eye, 
  UserCheck, Plus, Copy, Trash2, Check, CreditCard, ChevronDown, ChevronRight,
  Settings, Lock, Unlock, ArrowLeftRight, Equal, Minus
} from 'lucide-react';
import { toast } from 'sonner';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import { roleSettingsApi, permissionsApi } from '../services/api';

// User Types (account-level roles)
const USER_TYPES = [
  {
    role: 'admin',
    label: 'Administrator',
    description: 'Full system access. Always lands on /admin.',
    icon: Shield,
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  },
  {
    role: 'member',
    label: 'Member',
    description: 'Standard user. Landing page determined by team role.',
    icon: User,
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  },
];

// Role icons mapping
const ROLE_ICONS = {
  admin: Shield,
  owner: Crown,
  product_manager: Briefcase,
  member: User,
  collaborator: UserCheck,
  billing_admin: CreditCard,
};

// Role badge colors
const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  owner: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  product_manager: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  member: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  collaborator: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  billing_admin: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
};

// Object display names
const OBJECT_LABELS = {
  products: 'Products',
  epics: 'Epics',
  features: 'Features',
  notes: 'Notes',
  releases: 'Releases',
  initiatives: 'Initiatives',
  goals: 'Goals',
  personas: 'Personas',
  tags: 'Tags',
  requests: 'Requests',
  architecture: 'Architecture',
  delivery: 'Delivery',
  team_members: 'Team Members',
};

// Capability display names
const CAPABILITY_LABELS = {
  use_ai_copilot: 'Use AI Co-pilot',
  reorder_priority: 'Reorder Priority',
  manage_billing: 'Manage Billing',
  manage_company: 'Manage Company',
};

// Role Comparison Table Component
const RoleComparisonTable = ({ role1, role2, objects, colors }) => {
  if (!role1 || !role2) return null;
  
  const actions = ['read', 'create', 'update', 'delete'];
  const actionLabels = { read: 'R', create: 'C', update: 'U', delete: 'D' };
  
  // Helper to compare permissions and return diff status
  const comparePermission = (obj, action) => {
    const has1 = role1.permissions?.[obj]?.[action] || false;
    const has2 = role2.permissions?.[obj]?.[action] || false;
    
    if (has1 === has2) return 'same';
    return has1 ? 'role1_only' : 'role2_only';
  };
  
  // Helper to compare capabilities
  const compareCapability = (cap) => {
    const has1 = role1.capabilities?.[cap] || false;
    const has2 = role2.capabilities?.[cap] || false;
    
    if (has1 === has2) return 'same';
    return has1 ? 'role1_only' : 'role2_only';
  };
  
  // Count differences
  let totalDiffs = 0;
  objects.forEach(obj => {
    actions.forEach(action => {
      if (comparePermission(obj, action) !== 'same') totalDiffs++;
    });
  });
  Object.keys(CAPABILITY_LABELS).forEach(cap => {
    if (compareCapability(cap) !== 'same') totalDiffs++;
  });
  
  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className={cn(
        "flex items-center gap-3 p-3 rounded-lg text-sm",
        totalDiffs === 0 ? "bg-green-50 dark:bg-green-950" : "bg-amber-50 dark:bg-amber-950"
      )}>
        {totalDiffs === 0 ? (
          <>
            <Equal className="w-5 h-5 text-green-600" />
            <span className="text-green-700 dark:text-green-300">
              These roles have identical permissions
            </span>
          </>
        ) : (
          <>
            <ArrowLeftRight className="w-5 h-5 text-amber-600" />
            <span className="text-amber-700 dark:text-amber-300">
              <strong>{totalDiffs}</strong> permission difference{totalDiffs !== 1 ? 's' : ''} found
            </span>
          </>
        )}
      </div>
      
      {/* Legend */}
      <div className="flex items-center gap-6 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-green-500" />
          <span className={colors.textMuted}>{role1.label || role1.role} has</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-red-400" />
          <span className={colors.textMuted}>{role2.label || role2.role} has</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-gray-400" />
          <span className={colors.textMuted}>Both have</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-gray-200" />
          <span className={colors.textMuted}>Neither has</span>
        </div>
      </div>
      
      {/* Comparison Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className={cn("border-b", colors.borderPrimary, colors.bgTertiary)}>
              <th className={cn("text-left py-3 px-4 font-medium", colors.textSecondary)}>
                Permission
              </th>
              <th colSpan={4} className="text-center py-3 px-2">
                <div className="flex items-center justify-center gap-1">
                  <Badge className={cn("text-xs", ROLE_COLORS[role1.role] || ROLE_COLORS.viewer)}>
                    {role1.label || role1.role}
                  </Badge>
                </div>
                <div className="flex justify-center gap-2 mt-1.5 text-[10px] font-normal text-gray-400">
                  {actions.map(a => <span key={a} className="w-6">{actionLabels[a]}</span>)}
                </div>
              </th>
              <th className="py-3 px-2 text-center">
                <span className={colors.textMuted}>vs</span>
              </th>
              <th colSpan={4} className="text-center py-3 px-2">
                <div className="flex items-center justify-center gap-1">
                  <Badge className={cn("text-xs", ROLE_COLORS[role2.role] || ROLE_COLORS.viewer)}>
                    {role2.label || role2.role}
                  </Badge>
                </div>
                <div className="flex justify-center gap-2 mt-1.5 text-[10px] font-normal text-gray-400">
                  {actions.map(a => <span key={a} className="w-6">{actionLabels[a]}</span>)}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Object Permissions */}
            {objects.map(obj => {
              const hasDiff = actions.some(a => comparePermission(obj, a) !== 'same');
              return (
                <tr 
                  key={obj} 
                  className={cn(
                    "border-b last:border-0",
                    colors.borderPrimary,
                    hasDiff && "bg-amber-50/50 dark:bg-amber-950/20"
                  )}
                >
                  <td className={cn("py-2.5 px-4 font-medium", colors.textSecondary)}>
                    {OBJECT_LABELS[obj] || obj}
                    {hasDiff && <span className="ml-1.5 text-amber-500">*</span>}
                  </td>
                  {/* Role 1 permissions */}
                  {actions.map(action => {
                    const status = comparePermission(obj, action);
                    const has1 = role1.permissions?.[obj]?.[action] || false;
                    return (
                      <td key={`r1-${action}`} className="text-center py-2.5 px-1">
                        {has1 ? (
                          <Check className={cn(
                            "w-4 h-4 mx-auto",
                            status === 'same' ? "text-gray-400" : "text-green-500"
                          )} />
                        ) : (
                          <X className="w-4 h-4 text-gray-300 mx-auto" />
                        )}
                      </td>
                    );
                  })}
                  {/* Divider */}
                  <td className={cn("py-2.5 px-2 text-center", colors.borderPrimary)} style={{ borderLeft: '1px solid', borderRight: '1px solid' }}>
                    {hasDiff ? (
                      <span className="text-amber-500 text-xs font-bold">≠</span>
                    ) : (
                      <span className="text-gray-400 text-xs">=</span>
                    )}
                  </td>
                  {/* Role 2 permissions */}
                  {actions.map(action => {
                    const status = comparePermission(obj, action);
                    const has2 = role2.permissions?.[obj]?.[action] || false;
                    return (
                      <td key={`r2-${action}`} className="text-center py-2.5 px-1">
                        {has2 ? (
                          <Check className={cn(
                            "w-4 h-4 mx-auto",
                            status === 'same' ? "text-gray-400" : "text-red-400"
                          )} />
                        ) : (
                          <X className="w-4 h-4 text-gray-300 mx-auto" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            
            {/* Capabilities Section */}
            <tr className={cn("border-t-2", colors.borderPrimary, colors.bgTertiary)}>
              <td colSpan={10} className={cn("py-2 px-4 font-medium text-sm", colors.textPrimary)}>
                Capabilities
              </td>
            </tr>
            {Object.entries(CAPABILITY_LABELS).map(([capKey, capLabel]) => {
              const status = compareCapability(capKey);
              const has1 = role1.capabilities?.[capKey] || false;
              const has2 = role2.capabilities?.[capKey] || false;
              const hasDiff = status !== 'same';
              
              return (
                <tr 
                  key={capKey}
                  className={cn(
                    "border-b last:border-0",
                    colors.borderPrimary,
                    hasDiff && "bg-amber-50/50 dark:bg-amber-950/20"
                  )}
                >
                  <td className={cn("py-2.5 px-4 font-medium", colors.textSecondary)}>
                    {capLabel}
                    {hasDiff && <span className="ml-1.5 text-amber-500">*</span>}
                  </td>
                  <td colSpan={4} className="text-center py-2.5">
                    {has1 ? (
                      <Check className={cn(
                        "w-5 h-5 mx-auto",
                        status === 'same' ? "text-gray-400" : "text-green-500"
                      )} />
                    ) : (
                      <X className="w-5 h-5 text-gray-300 mx-auto" />
                    )}
                  </td>
                  <td className={cn("py-2.5 px-2 text-center", colors.borderPrimary)} style={{ borderLeft: '1px solid', borderRight: '1px solid' }}>
                    {hasDiff ? (
                      <span className="text-amber-500 text-xs font-bold">≠</span>
                    ) : (
                      <span className="text-gray-400 text-xs">=</span>
                    )}
                  </td>
                  <td colSpan={4} className="text-center py-2.5">
                    {has2 ? (
                      <Check className={cn(
                        "w-5 h-5 mx-auto",
                        status === 'same' ? "text-gray-400" : "text-red-400"
                      )} />
                    ) : (
                      <X className="w-5 h-5 text-gray-300 mx-auto" />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Permission Matrix Component (extracted for react/no-unstable-nested-components)
const PermissionMatrix = ({ roles: matrixRoles, objects: matrixObjects, colors }) => {
  const actions = ['read', 'create', 'update', 'delete'];
  
  const getRoleIcon = (role) => {
    const Icon = ROLE_ICONS[role] || User;
    return <Icon className="w-4 h-4" />;
  };
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className={cn("border-b", colors.borderPrimary)}>
            <th className={cn("text-left py-2 px-3 font-medium sticky left-0", colors.bgCard, colors.textSecondary)}>
              Object
            </th>
            {matrixRoles.map(role => (
              <th key={role.role} colSpan={4} className="text-center py-2 px-2">
                <div className="flex items-center justify-center gap-1">
                  {getRoleIcon(role.role)}
                  <span className={cn("text-xs font-medium", colors.textPrimary)}>
                    {role.label?.split(' ')[0] || role.role}
                  </span>
                </div>
                <div className="flex justify-center gap-1 mt-1 text-[10px] text-gray-400">
                  {actions.map(a => <span key={a} className="w-6">{a[0].toUpperCase()}</span>)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrixObjects.map(obj => (
            <tr key={obj} className={cn("border-b", colors.borderPrimary)}>
              <td className={cn("py-2 px-3 font-medium sticky left-0", colors.bgCard, colors.textSecondary)}>
                {OBJECT_LABELS[obj] || obj}
              </td>
              {matrixRoles.map(role => (
                <React.Fragment key={`${role.role}-${obj}`}>
                  {actions.map(action => {
                    const hasPermission = role.permissions?.[obj]?.[action];
                    return (
                      <td key={action} className="text-center py-2 px-1">
                        {hasPermission ? (
                          <Check className="w-4 h-4 text-green-500 mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-red-300 mx-auto" />
                        )}
                      </td>
                    );
                  })}
                </React.Fragment>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AdminRolesPage = () => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [roles, setRoles] = useState([]);
  const [memberRoles, setMemberRoles] = useState([]);
  const [objects, setObjects] = useState([]);
  const [systemRoles, setSystemRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Edit modal state
  const [editingRole, setEditingRole] = useState(null);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createData, setCreateData] = useState({
    role: '',
    label: '',
    description: '',
    default_url: '/products',
    clone_from: null
  });
  
  // Expanded sections in permission editor
  const [expandedSections, setExpandedSections] = useState({
    permissions: true,
    capabilities: true,
    fieldPermissions: false
  });
  
  // Compare feature state
  const [compareRole1, setCompareRole1] = useState(null);
  const [compareRole2, setCompareRole2] = useState(null);

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await roleSettingsApi.list();
      setRoles(response.data.roles || []);
      setMemberRoles(response.data.member_roles || []);
      setObjects(response.data.objects || []);
      setSystemRoles(response.data.system_roles || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
      toast.error('Failed to load role settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRole = (role) => {
    setEditData({
      ...role,
      permissions: role.permissions || {},
      field_permissions: role.field_permissions || {},
      capabilities: role.capabilities || {}
    });
    setEditingRole(role.role);
  };

  const handleCancelEdit = () => {
    setEditingRole(null);
    setEditData(null);
  };

  const handleSaveRole = async () => {
    if (!editData) return;
    
    try {
      setSaving(true);
      await roleSettingsApi.update(editingRole, {
        default_url: editData.default_url,
        label: editData.label,
        description: editData.description,
        is_paid_role: editData.is_paid_role,
        permissions: editData.permissions,
        field_permissions: editData.field_permissions,
        capabilities: editData.capabilities
      });
      toast.success(`Role "${editData.label}" updated successfully`);
      setEditingRole(null);
      setEditData(null);
      await loadRoles();
    } catch (err) {
      console.error('Failed to update role:', err);
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleCloneRole = async (role) => {
    try {
      const response = await roleSettingsApi.clone(role);
      toast.success(`Created "${response.data.role.label}"`);
      await loadRoles();
      // Open edit modal for the new role
      handleEditRole(response.data.role);
    } catch (err) {
      console.error('Failed to clone role:', err);
      toast.error(err.response?.data?.detail || 'Failed to clone role');
    }
  };

  const handleDeleteRole = async (role) => {
    if (!confirm(`Are you sure you want to delete the "${role}" role?`)) return;
    
    try {
      await roleSettingsApi.delete(role);
      toast.success('Role deleted');
      await loadRoles();
    } catch (err) {
      console.error('Failed to delete role:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  const handleCreateRole = async () => {
    if (!createData.role || !createData.label) {
      toast.error('Role ID and Label are required');
      return;
    }
    
    try {
      setSaving(true);
      const response = await roleSettingsApi.create(createData);
      toast.success(`Created role "${response.data.role.label}"`);
      setShowCreateModal(false);
      setCreateData({ role: '', label: '', description: '', default_url: '/products', clone_from: null });
      await loadRoles();
      // Open edit modal for the new role
      handleEditRole(response.data.role);
    } catch (err) {
      console.error('Failed to create role:', err);
      toast.error(err.response?.data?.detail || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (object, action) => {
    if (!editData) return;
    
    const currentValue = editData.permissions?.[object]?.[action] || false;
    const newValue = !currentValue;
    
    setEditData(prev => {
      const newPerms = {
        ...prev.permissions,
        [object]: {
          ...prev.permissions[object],
          [action]: newValue
        }
      };
      
      // Rule: If enabling Create, Update, or Delete - automatically enable Read
      if (newValue && action !== 'read') {
        newPerms[object].read = true;
      }
      
      // Rule: If disabling Read - automatically disable Create, Update, Delete
      if (!newValue && action === 'read') {
        newPerms[object].create = false;
        newPerms[object].update = false;
        newPerms[object].delete = false;
      }
      
      return { ...prev, permissions: newPerms };
    });
  };

  const toggleFieldPermission = (field) => {
    if (!editData) return;
    
    setEditData(prev => ({
      ...prev,
      field_permissions: {
        ...prev.field_permissions,
        [field]: !prev.field_permissions[field]
      }
    }));
  };

  const toggleCapability = (capability) => {
    if (!editData) return;
    
    setEditData(prev => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        [capability]: !prev.capabilities[capability]
      }
    }));
  };

  const getRoleIcon = (role) => {
    const Icon = ROLE_ICONS[role] || User;
    return <Icon className="w-4 h-4" />;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Role Settings" />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className={cn("text-2xl font-bold", colors.textPrimary)}>Role Settings</h2>
                <p className={cn("text-sm mt-1", colors.textMuted)}>
                  Configure permissions and access for each role.
                </p>
              </div>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <Tabs defaultValue="roles" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="roles">Roles</TabsTrigger>
                  <TabsTrigger value="matrix">Permission Matrix</TabsTrigger>
                  <TabsTrigger value="compare" className="flex items-center gap-1.5">
                    <ArrowLeftRight className="w-3.5 h-3.5" />
                    Compare
                  </TabsTrigger>
                </TabsList>

                {/* Roles Tab */}
                <TabsContent value="roles" className="space-y-6">
                  {/* User Types Section */}
                  <Card className={cn(colors.bgCard, colors.borderPrimary)}>
                    <CardHeader>
                      <CardTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
                        <Shield className="w-5 h-5 text-blue-500" />
                        User Types
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={cn("text-sm mb-4", colors.textMuted)}>
                        Account-level roles that determine primary access level.
                      </p>
                      <div className="space-y-3">
                        {USER_TYPES.map((userType) => {
                          const Icon = userType.icon;
                          return (
                            <div 
                              key={userType.role}
                              className={cn(
                                "flex items-center gap-4 px-4 py-3 rounded-lg",
                                colors.bgTertiary
                              )}
                            >
                              <Badge className={cn("flex items-center gap-1.5 w-fit", userType.color)}>
                                <Icon className="w-4 h-4" />
                                <span className="capitalize">{userType.role}</span>
                              </Badge>
                              <div>
                                <p className={cn("font-medium", colors.textPrimary)}>{userType.label}</p>
                                <p className={cn("text-xs", colors.textMuted)}>{userType.description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Member Roles Section */}
                  <Card className={cn(colors.bgCard, colors.borderPrimary)}>
                    <CardHeader>
                      <CardTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
                        <Users className="w-5 h-5 text-purple-500" />
                        Member Roles
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={cn("text-sm mb-4", colors.textMuted)}>
                        Team-level roles with configurable permissions. Click Edit to modify a role&apos;s permissions.
                      </p>
                      
                      {/* Table Header */}
                      <div className={cn(
                        "grid grid-cols-12 gap-4 px-4 py-2 text-sm font-medium rounded-t-lg border-b",
                        colors.bgTertiary, colors.borderPrimary
                      )}>
                        <div className={cn("col-span-2", colors.textMuted)}>Role</div>
                        <div className={cn("col-span-3", colors.textMuted)}>Description</div>
                        <div className={cn("col-span-2 text-center", colors.textMuted)}>Type</div>
                        <div className={cn("col-span-2", colors.textMuted)}>Default URL</div>
                        <div className={cn("col-span-3 text-right", colors.textMuted)}>Actions</div>
                      </div>
                      
                      {/* Table Rows */}
                      <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {memberRoles.map((role) => (
                          <div 
                            key={role.role}
                            className={cn(
                              "grid grid-cols-12 gap-4 px-4 py-3 items-center transition-colors",
                              "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                            )}
                          >
                            {/* Role Badge */}
                            <div className="col-span-2">
                              <Badge className={cn("flex items-center gap-1.5 w-fit", ROLE_COLORS[role.role] || ROLE_COLORS.collaborator)}>
                                {getRoleIcon(role.role)}
                                <span className="capitalize text-xs">{role.label || role.role.replace('_', ' ')}</span>
                              </Badge>
                            </div>
                            
                            {/* Description */}
                            <div className="col-span-3">
                              <p className={cn("text-sm", colors.textMuted)}>{role.description}</p>
                            </div>
                            
                            {/* Type Badges */}
                            <div className="col-span-2 flex justify-center gap-1">
                              {role.is_system_role && (
                                <Badge variant="outline" className="text-xs">
                                  <Lock className="w-3 h-3 mr-1" />
                                  System
                                </Badge>
                              )}
                              {role.is_paid_role && (
                                <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs">
                                  Paid
                                </Badge>
                              )}
                              {!role.is_system_role && !role.is_paid_role && (
                                <Badge variant="outline" className="text-xs text-gray-400">
                                  <Unlock className="w-3 h-3 mr-1" />
                                  Custom
                                </Badge>
                              )}
                            </div>
                            
                            {/* Default URL */}
                            <div className="col-span-2">
                              <code className={cn("px-2 py-1 rounded text-xs font-mono", colors.bgTertiary)}>
                                {role.default_url}
                              </code>
                            </div>
                            
                            {/* Actions */}
                            <div className="col-span-3 flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditRole(role)}
                                className={colors.borderPrimary}
                              >
                                <Edit2 className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCloneRole(role.role)}
                                className={colors.borderPrimary}
                                title="Clone Role"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              {!role.is_system_role && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteRole(role.role)}
                                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                  title="Delete Role"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Permission Matrix Tab */}
                <TabsContent value="matrix">
                  <Card className={cn(colors.bgCard, colors.borderPrimary)}>
                    <CardHeader>
                      <CardTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
                        <Settings className="w-5 h-5 text-blue-500" />
                        Permission Matrix
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={cn("text-sm mb-4", colors.textMuted)}>
                        Overview of all role permissions. C = Create, R = Read, U = Update, D = Delete.
                      </p>
                      <PermissionMatrix roles={memberRoles} objects={objects} colors={colors} />
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Compare Roles Tab */}
                <TabsContent value="compare">
                  <Card className={cn(colors.bgCard, colors.borderPrimary)}>
                    <CardHeader>
                      <CardTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
                        <ArrowLeftRight className="w-5 h-5 text-blue-500" />
                        Compare Roles
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={cn("text-sm mb-4", colors.textMuted)}>
                        Select two roles to see a side-by-side comparison of their permissions.
                      </p>
                      
                      {/* Role Selectors */}
                      <div className="flex gap-4 mb-6">
                        <div className="flex-1">
                          <Label className={cn("mb-2 block", colors.textSecondary)}>Role 1</Label>
                          <select
                            value={compareRole1 || ''}
                            onChange={(e) => setCompareRole1(e.target.value || null)}
                            className={cn(
                              "w-full px-3 py-2 rounded-md border text-sm",
                              colors.bgInput, colors.borderPrimary, colors.textPrimary
                            )}
                          >
                            <option value="">Select a role...</option>
                            {memberRoles.map(role => (
                              <option key={role.role} value={role.role}>
                                {role.label || role.role}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="flex items-end pb-2">
                          <ArrowLeftRight className={cn("w-5 h-5", colors.textMuted)} />
                        </div>
                        
                        <div className="flex-1">
                          <Label className={cn("mb-2 block", colors.textSecondary)}>Role 2</Label>
                          <select
                            value={compareRole2 || ''}
                            onChange={(e) => setCompareRole2(e.target.value || null)}
                            className={cn(
                              "w-full px-3 py-2 rounded-md border text-sm",
                              colors.bgInput, colors.borderPrimary, colors.textPrimary
                            )}
                          >
                            <option value="">Select a role...</option>
                            {memberRoles.map(role => (
                              <option key={role.role} value={role.role}>
                                {role.label || role.role}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Comparison Table */}
                      {compareRole1 && compareRole2 ? (
                        <RoleComparisonTable 
                          role1={memberRoles.find(r => r.role === compareRole1)}
                          role2={memberRoles.find(r => r.role === compareRole2)}
                          objects={objects}
                          colors={colors}
                        />
                      ) : (
                        <div className={cn(
                          "py-12 text-center border-2 border-dashed rounded-lg",
                          colors.borderPrimary
                        )}>
                          <ArrowLeftRight className={cn("w-12 h-12 mx-auto mb-3", colors.textMuted)} />
                          <p className={colors.textMuted}>
                            Select two roles above to compare their permissions
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </main>
      </div>
      
      {/* Edit Role Modal */}
      {editingRole && editData && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/70" onClick={handleCancelEdit} />
          <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
            <Card className={cn("w-full max-w-4xl my-8", colors.bgCard)}>
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge className={cn("flex items-center gap-1.5", ROLE_COLORS[editData.role] || ROLE_COLORS.viewer)}>
                    {getRoleIcon(editData.role)}
                    <span className="capitalize">{editData.role.replace('_', ' ')}</span>
                  </Badge>
                  <CardTitle className={colors.textPrimary}>Edit Role</CardTitle>
                  {editData.is_system_role && (
                    <Badge variant="outline" className="text-xs">
                      <Lock className="w-3 h-3 mr-1" />
                      System Role
                    </Badge>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              
              <CardContent className="space-y-6 max-h-[70vh] overflow-y-auto">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={colors.textSecondary}>Label</Label>
                    <Input
                      value={editData.label}
                      onChange={(e) => setEditData(prev => ({ ...prev, label: e.target.value }))}
                      className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                    />
                  </div>
                  <div>
                    <Label className={colors.textSecondary}>Default URL</Label>
                    <Input
                      value={editData.default_url}
                      onChange={(e) => setEditData(prev => ({ ...prev, default_url: e.target.value }))}
                      className={cn("mt-1 font-mono", colors.bgInput, colors.borderPrimary)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className={colors.textSecondary}>Description</Label>
                    <Input
                      value={editData.description}
                      onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                      className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                    />
                  </div>
                  <div className="col-span-2 flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div>
                      <Label className={colors.textSecondary}>Paid Role</Label>
                      <p className={cn("text-xs", colors.textMuted)}>This role requires a paid seat in the organization</p>
                    </div>
                    <Switch
                      checked={editData.is_paid_role || false}
                      onCheckedChange={(checked) => setEditData(prev => ({ ...prev, is_paid_role: checked }))}
                      className="data-[state=checked]:bg-green-500"
                    />
                  </div>
                </div>

                {/* Permissions Section */}
                <div>
                  <button
                    onClick={() => toggleSection('permissions')}
                    className={cn("flex items-center gap-2 w-full text-left font-medium py-2", colors.textPrimary)}
                  >
                    {expandedSections.permissions ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    Object Permissions (CRUD)
                  </button>
                  
                  {expandedSections.permissions && (
                    <div className={cn("mt-2 p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                      <p className={cn("text-xs mb-3", colors.textMuted)}>
                        Read is required for Create, Update, or Delete. Disabling Read will disable all other permissions.
                      </p>
                      <div className="grid grid-cols-5 gap-2 text-xs font-medium text-center mb-2">
                        <div className={cn("text-left", colors.textMuted)}>Object</div>
                        <div className={colors.textMuted}>Read</div>
                        <div className={colors.textMuted}>Create</div>
                        <div className={colors.textMuted}>Update</div>
                        <div className={colors.textMuted}>Delete</div>
                      </div>
                      
                      {objects.map(obj => (
                        <div key={obj} className="grid grid-cols-5 gap-2 py-2 border-t border-gray-200 dark:border-gray-700">
                          <div className={cn("text-sm", colors.textSecondary)}>
                            {OBJECT_LABELS[obj] || obj}
                          </div>
                          {['read', 'create', 'update', 'delete'].map(action => (
                            <div key={action} className="flex justify-center">
                              <Switch
                                checked={editData.permissions?.[obj]?.[action] || false}
                                onCheckedChange={() => togglePermission(obj, action)}
                                className="data-[state=checked]:bg-green-500"
                              />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Field Permissions Section */}
                <div>
                  <button
                    onClick={() => toggleSection('fieldPermissions')}
                    className={cn("flex items-center gap-2 w-full text-left font-medium py-2", colors.textPrimary)}
                  >
                    {expandedSections.fieldPermissions ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    Field-Level Exceptions
                  </button>
                  
                  {expandedSections.fieldPermissions && (
                    <div className={cn("mt-2 p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                      <p className={cn("text-xs mb-3", colors.textMuted)}>
                        These override object-level permissions for specific fields.
                      </p>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={cn("text-sm font-medium", colors.textSecondary)}>features.status</p>
                            <p className={cn("text-xs", colors.textMuted)}>Allow moving Kanban cards</p>
                          </div>
                          <Switch
                            checked={editData.field_permissions?.['features.status'] || false}
                            onCheckedChange={() => toggleFieldPermission('features.status')}
                            className="data-[state=checked]:bg-green-500"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={cn("text-sm font-medium", colors.textSecondary)}>features.devlog</p>
                            <p className={cn("text-xs", colors.textMuted)}>Allow adding dev log entries</p>
                          </div>
                          <Switch
                            checked={editData.field_permissions?.['features.devlog'] || false}
                            onCheckedChange={() => toggleFieldPermission('features.devlog')}
                            className="data-[state=checked]:bg-green-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Capabilities Section */}
                <div>
                  <button
                    onClick={() => toggleSection('capabilities')}
                    className={cn("flex items-center gap-2 w-full text-left font-medium py-2", colors.textPrimary)}
                  >
                    {expandedSections.capabilities ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    Capabilities
                  </button>
                  
                  {expandedSections.capabilities && (
                    <div className={cn("mt-2 p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(CAPABILITY_LABELS).map(([key, label]) => (
                          <div key={key} className="flex items-center justify-between">
                            <span className={cn("text-sm", colors.textSecondary)}>{label}</span>
                            <Switch
                              checked={editData.capabilities?.[key] || false}
                              onCheckedChange={() => toggleCapability(key)}
                              className="data-[state=checked]:bg-green-500"
                            />
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Label className={cn("text-sm", colors.textSecondary)}>Can Invite Roles</Label>
                        <p className={cn("text-xs mb-2", colors.textMuted)}>
                          Which roles this role can invite to the team.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {['owner', 'product_manager', 'collaborator', 'viewer', 'billing_admin'].map(role => {
                            const isSelected = editData.capabilities?.invite_roles?.includes(role);
                            return (
                              <Badge
                                key={role}
                                variant={isSelected ? 'default' : 'outline'}
                                className={cn(
                                  "cursor-pointer transition-colors",
                                  isSelected ? 'bg-blue-500 text-white' : ''
                                )}
                                onClick={() => {
                                  const currentRoles = editData.capabilities?.invite_roles || [];
                                  const newRoles = isSelected
                                    ? currentRoles.filter(r => r !== role)
                                    : [...currentRoles, role];
                                  setEditData(prev => ({
                                    ...prev,
                                    capabilities: {
                                      ...prev.capabilities,
                                      invite_roles: newRoles
                                    }
                                  }));
                                }}
                              >
                                {role.replace('_', ' ')}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
              
              <div className={cn("flex justify-end gap-3 p-6 border-t", colors.borderPrimary)}>
                <Button variant="outline" onClick={handleCancelEdit} className={colors.borderPrimary}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveRole}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
      
      {/* Create Role Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/70" onClick={() => setShowCreateModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Card className={cn("w-full max-w-md", colors.bgCard)}>
              <CardHeader>
                <CardTitle className={colors.textPrimary}>Create Custom Role</CardTitle>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div>
                  <Label className={colors.textSecondary}>Role ID *</Label>
                  <Input
                    value={createData.role}
                    onChange={(e) => setCreateData(prev => ({ 
                      ...prev, 
                      role: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                    }))}
                    placeholder="custom_role"
                    className={cn("mt-1 font-mono", colors.bgInput, colors.borderPrimary)}
                  />
                  <p className={cn("text-xs mt-1", colors.textMuted)}>
                    Lowercase letters, numbers, and underscores only
                  </p>
                </div>
                
                <div>
                  <Label className={colors.textSecondary}>Label *</Label>
                  <Input
                    value={createData.label}
                    onChange={(e) => setCreateData(prev => ({ ...prev, label: e.target.value }))}
                    placeholder="Custom Role"
                    className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                  />
                </div>
                
                <div>
                  <Label className={colors.textSecondary}>Description</Label>
                  <Input
                    value={createData.description}
                    onChange={(e) => setCreateData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Role description..."
                    className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                  />
                </div>
                
                <div>
                  <Label className={colors.textSecondary}>Default URL</Label>
                  <Input
                    value={createData.default_url}
                    onChange={(e) => setCreateData(prev => ({ ...prev, default_url: e.target.value }))}
                    className={cn("mt-1 font-mono", colors.bgInput, colors.borderPrimary)}
                  />
                </div>
                
                <div>
                  <Label className={colors.textSecondary}>Clone Permissions From</Label>
                  <select
                    value={createData.clone_from || ''}
                    onChange={(e) => setCreateData(prev => ({ ...prev, clone_from: e.target.value || null }))}
                    className={cn(
                      "mt-1 w-full rounded-md border px-3 py-2",
                      colors.bgInput, colors.borderPrimary, colors.textPrimary
                    )}
                  >
                    <option value="">Start with minimal permissions</option>
                    {memberRoles.map(role => (
                      <option key={role.role} value={role.role}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </CardContent>
              
              <div className={cn("flex justify-end gap-3 p-6 border-t", colors.borderPrimary)}>
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateModal(false)} 
                  className={colors.borderPrimary}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateRole}
                  disabled={saving || !createData.role || !createData.label}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Role
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRolesPage;
